import React, { useState } from 'react';
import { peep_client } from '~/src/api/peep';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAbort } from '~src/utils/useAbort';
import { Button, Text, Paper, Stack, Title, Alert } from '@mantine/core';
import { Mail, AlertCircle, Check } from 'lucide-react';
import { HTTPError } from 'ky';
import { ApiErrorResponseSchema } from '~src/types/errors';

type CheckEmailState =
  | { status: 'idle' }
  | { status: 'resending' }
  | { status: 'resent' }
  | { status: 'error'; error: string };

export default function CheckEmail() {
  const [state, setState] = useState<CheckEmailState>({ status: 'idle' });
  const navigate = useNavigate();
  const location = useLocation();
  const abortRef = useAbort();

  const email = location.state?.email;

  if (!email) {
    navigate('/signup');
    return null;
  }

  const handleResend = async () => {
    setState({ status: 'resending' });

    try {
      await peep_client.users.requestMagicLink(email, { signal: abortRef.current.signal });
      setState({ status: 'resent' });
    } catch (e) {
      console.error(e);

      let errorMsg = 'Failed to resend email. Please try again.';

      if (e instanceof HTTPError) {
        try {
          const body = await e.response.json();
          const parsed = ApiErrorResponseSchema.parse(body);
          errorMsg = parsed.message || errorMsg;
        } catch {
          // Use default error
        }
      }

      setState({ status: 'error', error: errorMsg });
    }
  };

  const isLoading = state.status === 'resending';
  const error = state.status === 'error' ? state.error : null;
  const resent = state.status === 'resent';

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        minHeight: '100dvh',
        backgroundColor: 'var(--global-gray-lighter-7)',
        padding: '2rem',
      }}
    >
      <Paper
        style={{
          width: 'min(500px, 90vw)',
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '2rem',
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
          border: '1px solid #f0f0f0',
        }}
      >
        <Stack gap="lg">
          <div style={{ textAlign: 'center' }}>
            <div
              style={{
                display: 'inline-flex',
                padding: '1rem',
                backgroundColor: 'var(--global-main-lighter-8)',
                borderRadius: '50%',
                marginBottom: '1rem',
              }}
            >
              <Mail size={32} color="var(--global-main)" />
            </div>
            <Title order={2} style={{ fontFamily: 'Lato', color: '#333', marginBottom: '0.5rem' }}>
              Check your email
            </Title>
            <Text size="sm" style={{ fontFamily: 'Lato', color: '#666' }}>
              We've sent a sign-in code to
            </Text>
            <Text size="sm" style={{ fontFamily: 'Lato', color: '#333', fontWeight: 600, marginTop: '0.25rem' }}>
              {email}
            </Text>
          </div>

          {error && (
            <Alert icon={<AlertCircle size={16} />} color="red" style={{ fontFamily: 'Lato' }}>
              {error}
            </Alert>
          )}

          {resent && (
            <Alert icon={<Check size={16} />} color="green" style={{ fontFamily: 'Lato' }}>
              New code sent! Check your inbox.
            </Alert>
          )}

          <div
            style={{
              padding: '1rem',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              textAlign: 'center',
            }}
          >
            <Text size="sm" style={{ fontFamily: 'Lato', color: '#666', fontWeight: 600, marginBottom: '0.5rem' }}>
              To verify your account:
            </Text>
            <Text size="sm" style={{ fontFamily: 'Lato', color: '#666' }}>
              1. Go to the sign in page
            </Text>
            <Text size="sm" style={{ fontFamily: 'Lato', color: '#666' }}>
              2. Switch to "With email code" mode
            </Text>
            <Text size="sm" style={{ fontFamily: 'Lato', color: '#666' }}>
              3. Enter your code to sign in (this verifies your account)
            </Text>
          </div>

          <Stack gap="sm">
            <Button
              onClick={() => navigate('/signin')}
              size="md"
              style={{
                backgroundColor: 'var(--global-main)',
                fontFamily: 'Lato',
                fontWeight: '600',
                borderRadius: '8px',
              }}
              fullWidth
            >
              Go to Sign In
            </Button>

            <Button
              onClick={handleResend}
              variant="subtle"
              disabled={isLoading}
              loading={isLoading}
              size="md"
              style={{
                fontFamily: 'Lato',
                color: 'var(--global-main)',
              }}
              fullWidth
            >
              Didn't receive it? Resend code
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </div>
  );
}
